import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  HelpCircle,
  Search,
  Train,
  BookOpen,
  Users,
  ClipboardCheck,
  AlertCircle,
  ExternalLink,
  ArrowRight,
  Brain,
  Eye,
  Timer,
  Shield,
  Target,
  CheckCircle,
  XCircle,
  MessageSquare,
  Activity,
  FileText,
  RefreshCw,
  GraduationCap,
  Scale,
  Lightbulb,
  ListChecks,
  Clock,
  Route,
  Award,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { PageLayout } from '../components/Layout';
import { SEO, PageSEO } from '../components/SEO';

const TDF_MAIN = 'https://www.traindriverfoundation.com';
const TDF_ONLINE = 'https://www.traindriverfoundation.com/online-assessment';
const TDF_INTERVIEW = 'https://www.traindriverfoundation.com/interview';
const RSSB = 'https://www.rssb.co.uk';
const ORR = 'https://www.orr.gov.uk';

const TEST_LINKS = [
  {
    title: 'Group Bourdon Practice',
    description: 'Concentration, selective attention and visual scanning.',
    to: '/tests/group-bourdon',
    icon: Target,
  },
  {
    title: 'TRP / Rules & Procedures Practice',
    description: 'Learning, recalling and applying rule-based information.',
    to: '/tests/trp',
    icon: BookOpen,
  },
  {
    title: 'ATAVT Style Perception Practice',
    description: 'Fast visual perception and traffic-scene decision making.',
    to: '/tests/atAVT',
    icon: Eye,
  },
  {
    title: 'Vigilance Practice',
    description: 'Sustained attention over repetitive safety-critical tasks.',
    to: '/tests/vigilance',
    icon: Timer,
  },
  {
    title: 'Situational Judgement Preparation',
    description: 'Decision-making, judgement and behavioural aptitude practice.',
    href: TDF_ONLINE,
    icon: Shield,
  },
  {
    title: 'MMI Interview Preparation',
    description: 'Structured evidence for behavioural and safety-critical interview areas.',
    href: TDF_INTERVIEW,
    icon: MessageSquare,
  },
];

const QUICK_STATS = [
  { label: 'Current standard', value: 'RIS-3751-TOM Issue 4', icon: FileText },
  { label: 'Result validity', value: '5 years', icon: Clock },
  { label: 'New candidate attempts', value: 'Up to 3', icon: RefreshCw },
  { label: 'Assessment focus', value: 'Safe train-driving aptitude', icon: Shield },
];

const FAQ_CATEGORIES = [
  {
    id: 'overview',
    name: 'Psychometrics Overview',
    icon: Brain,
    color: 'bg-blue-500',
    questions: [
      {
        id: 'overview-1',
        question: 'What are train driver psychometric tests?',
        answer: `Train driver psychometric tests are structured assessments used during trainee train driver recruitment to measure safety-related aptitude. They are not general intelligence tests and they are not designed to test railway knowledge. They assess whether a candidate has the core cognitive, psychomotor and behavioural aptitudes needed to have the potential to become a safe train driver after training.

The standardised GB mainline process is set out in RIS-3751-TOM Issue 4. The tests cover attention, vigilance, memory, reasoning, perception, reaction time, hand coordination, communication and behavioural aptitude.`,
      },
      {
        id: 'overview-2',
        question: 'Why do train companies use psychometric assessments?',
        answer: `Train driving is safety-critical work. Drivers must maintain concentration for long periods, notice important information, respond correctly to signals and instructions, manage routine without becoming complacent, and make safe decisions under pressure.

Psychometric assessment helps train operators deselect candidates who do not meet the minimum aptitude level required for safe train driving. It is one part of the wider recruitment process, alongside application screening, interviews, medical requirements and company-specific checks.`,
      },
      {
        id: 'overview-3',
        question: 'Are train driver psychometric tests hard?',
        answer: `They are challenging because they assess speed, accuracy, consistency and attention under pressure. Many candidates find the individual tasks simple in principle, but difficult when time limits, repetition and performance standards are added.

The difficulty is not just understanding what to do. The challenge is performing accurately, consistently and calmly when the assessment is timed, unfamiliar or mentally tiring.`,
      },
      {
        id: 'overview-4',
        question: 'Are the tests the same for every train company?',
        answer: `The standardised psychometric assessment process is defined by RIS-3751-TOM, but train companies may structure their recruitment process differently. Some may use additional online tests, extra interviews, enhanced standards, company-specific exercises or staged assessment days.

The core industry psychometric areas remain broadly consistent, but the order, location, provider, cut-off approach and additional recruitment stages may vary by operator.`,
      },
      {
        id: 'overview-5',
        question: 'Are train driver psychometric tests the same as online assessments?',
        answer: `Not always. Some train companies use early online screening tests before inviting candidates to the formal psychometric assessment centre. These online tests may include situational judgement, checking, concentration, verbal reasoning, numerical reasoning or personality-style questionnaires.

The formal train driver psychometric process is the standardised assessment used for train driver selection. Online screening tests may be separate company-specific stages used before the official assessment centre.`,
      },
      {
        id: 'overview-6',
        question: 'Is psychometric testing only used for trainee train drivers?',
        answer: `RIS-3751-TOM is designed for candidate train drivers who will drive on the GB mainline railway. It is not designed for general railway jobs or for other stages of employment once driver training has started.

Qualified drivers changing employer may be treated differently. Their previous driving experience, licence status, safety record and previous assessment history may be reviewed through an assessment needs analysis.`,
      },
    ],
  },
  {
    id: 'tests',
    name: 'The Individual Tests',
    icon: ListChecks,
    color: 'bg-indigo-500',
    questions: [
      {
        id: 'tests-1',
        question: 'What tests are included in the train driver psychometric assessment?',
        answer: `The standardised process includes assessment areas linked to specific tests:

• Attention: Group Bourdon and TEA-Occ
• Vigilance: WAFV Vigilance
• Memory: TRP1, Trainability for Rules and Procedures
• Reasoning: TRP2, Trainability for Rules and Procedures
• Perception: ATAVT
• Reaction time: WAFV and ATAVT
• Hand coordination: 2HAND
• Communication: Multi-Modal Interview, known as MMI
• Behavioural aptitudes: Situational Judgement Exercise, known as SJE, and MMI`,
      },
      {
        id: 'tests-2',
        question: 'What is the Group Bourdon test?',
        answer: `The Group Bourdon test is a paper-based attention and concentration test. Candidates usually search rows of dot patterns and mark target patterns while avoiding similar distractors.

It is designed to assess selective attention, visual scanning, concentration, accuracy and consistency. In train driver terms, this links to the ability to notice important information while filtering out irrelevant information.

Practise this skill with our Group Bourdon-style concentration tasks in the test area.`,
        links: [{ label: 'Practise Group Bourdon-style tests', to: '/tests/group-bourdon' }],
      },
      {
        id: 'tests-3',
        question: 'What is TEA-Occ?',
        answer: `TEA-Occ stands for Test of Everyday Attention - Occupational. It assesses attention in an occupational context. In train driver selection, it supports assessment of selective and divided attention.

This matters because drivers need to manage multiple sources of information, such as signals, lineside signs, cab indications, instructions, radio communication and the movement of the train.`,
      },
      {
        id: 'tests-4',
        question: 'What is the WAFV Vigilance test?',
        answer: `The WAFV Vigilance test is a computerised vigilance assessment. It measures the ability to remain alert and respond to infrequent signals over a sustained period.

This is directly relevant to train driving because safe performance depends on maintaining attention even during repetitive, quiet or routine periods.`,
        links: [{ label: 'Practise vigilance-style tasks', to: '/tests/vigilance' }],
      },
      {
        id: 'tests-5',
        question: 'What is TRP1?',
        answer: `TRP1 is part of the Trainability for Rules and Procedures assessment. It focuses on memory: learning, recalling and applying job-related information within time limits.

This is relevant because trainee drivers must learn significant amounts of rules, traction knowledge, route knowledge and operational procedures during training.`,
        links: [{ label: 'Practise rules and procedures tasks', to: '/tests/trp' }],
      },
      {
        id: 'tests-6',
        question: 'What is TRP2?',
        answer: `TRP2 is also part of the Trainability for Rules and Procedures assessment. It focuses more on reasoning, problem solving and applying information to make decisions.

This links to areas such as interpreting operational information, understanding instructions, diagnosing faults and applying procedures correctly.`,
        links: [{ label: 'Practise TRP-style reasoning', to: '/tests/trp' }],
      },
      {
        id: 'tests-7',
        question: 'What is ATAVT?',
        answer: `ATAVT stands for Adaptive Tachistoscopic Traffic Test. It is a computerised perception test. Candidates are shown traffic or visual scenes very briefly and must identify relevant objects or hazards.

In train driver selection, it assesses fast visual perception and the ability to make correct decisions from limited visual information. This links to identifying cues, risks and changes in a dynamic operating environment.`,
        links: [{ label: 'Practise ATAVT-style perception', to: '/tests/atAVT' }],
      },
      {
        id: 'tests-8',
        question: 'What is the 2HAND test?',
        answer: `2HAND is a computerised hand coordination test. It measures whether a candidate can make controlled movements in response to complex visual stimuli.

This is relevant because driving involves coordinated control, monitoring and response. It is not about being a gamer or having fast hands alone; it is about controlled, accurate and appropriate movement.`,
      },
      {
        id: 'tests-9',
        question: 'What is the SJE?',
        answer: `SJE stands for Situational Judgement Exercise. It assesses behavioural aptitude by asking candidates to respond to job-relevant scenarios.

For train driver candidates, the SJE usually explores judgement, safety awareness, rule compliance, communication, responsibility, honesty, concentration, dealing with pressure and how you prioritise safe outcomes.

TDF has a dedicated online assessment preparation area for SJE-style preparation.`,
        links: [{ label: 'Prepare for SJE-style online assessments', href: TDF_ONLINE }],
      },
      {
        id: 'tests-10',
        question: 'What is the MMI?',
        answer: `MMI stands for Multi-Modal Interview. It is a structured interview used with the SJE to assess behavioural aptitudes and oral communication.

The MMI usually explores real examples from your past experience. Candidates may be asked about concentration, following rules, handling pressure, emergency situations, communication, responsibility and working safely.

TDF has dedicated MMI interview preparation guidance.`,
        links: [{ label: 'Prepare for the MMI', href: TDF_INTERVIEW }],
      },
    ],
  },
  {
    id: 'passing',
    name: 'How to Pass',
    icon: Target,
    color: 'bg-green-500',
    questions: [
      {
        id: 'passing-1',
        question: 'How do I pass train driver psychometric tests?',
        answer: `You pass by demonstrating the required level of aptitude across the assessment areas. The best preparation is not memorising answers. It is building familiarity with the type of mental effort required: concentration, sustained attention, visual scanning, rule learning, reasoning, perception, reaction control and structured behavioural evidence.

A sensible preparation plan is:

• Understand the tests before practising
• Practise short, focused sessions regularly
• Prioritise accuracy before speed
• Build speed only when accuracy is stable
• Practise under timed conditions
• Review mistakes and identify patterns
• Avoid exhausting yourself immediately before assessment day
• Prepare strong real examples for the MMI
• Learn what safe railway behaviour looks like for SJE-style judgement questions`,
      },
      {
        id: 'passing-2',
        question: 'Can practice improve my chances?',
        answer: `Practice can improve familiarity, confidence, test-taking discipline and relevant underlying skills. It can help you understand the format, reduce anxiety and improve consistency.

However, no practice provider can guarantee a pass. The official assessment is designed to measure stable aptitudes relevant to safe train driving. Practice should be used to prepare properly, not to try to game the process.`,
      },
      {
        id: 'passing-3',
        question: 'How much should I practise before the assessment?',
        answer: `A good approach is to practise little and often. For most candidates, several focused sessions over a number of days or weeks is better than one long last-minute session.

Avoid doing so much practice that you become fatigued, frustrated or careless. Psychometric performance depends on calm, accurate and consistent execution.`,
      },
      {
        id: 'passing-4',
        question: 'Should I focus on speed or accuracy?',
        answer: `Accuracy comes first. Many candidates fail because they rush and make too many errors. In safety-critical assessment, fast but careless performance is not the aim.

Build your method first. Once you can perform accurately, gradually increase speed while keeping your error rate controlled.`,
      },
      {
        id: 'passing-5',
        question: 'What is the best way to practise Group Bourdon?',
        answer: `Use a consistent scanning method. Work row by row, avoid jumping around the page, and prioritise accuracy. Track your omissions and incorrect markings. Your goal is not simply to complete more items; your goal is to complete more items while keeping mistakes low.

Practise in short bursts, then review. If errors increase sharply as you speed up, slow down and rebuild control.`,
        links: [{ label: 'Practise Group Bourdon-style tests', to: '/tests/group-bourdon' }],
      },
      {
        id: 'passing-6',
        question: 'How do I prepare for vigilance tests?',
        answer: `Vigilance preparation is about sustained attention. Practise staying engaged during repetitive tasks without switching off. Reduce distractions. Work in a quiet place. Train yourself to keep scanning even when the task feels boring.

This matters because the train driver role includes long periods where nothing unusual happens, followed by moments where a correct response is critical.`,
        links: [{ label: 'Practise vigilance-style tests', to: '/tests/vigilance' }],
      },
      {
        id: 'passing-7',
        question: 'How do I prepare for TRP tests?',
        answer: `TRP preparation should focus on learning information accurately, identifying rules, recalling key details and applying them under time pressure.

Do not just read passively. Read, cover, recall, summarise, apply and check. Practise extracting the important rule from a passage and using it to answer questions correctly.`,
        links: [{ label: 'Practise TRP-style tasks', to: '/tests/trp' }],
      },
      {
        id: 'passing-8',
        question: 'How do I prepare for ATAVT?',
        answer: `ATAVT-style preparation should focus on fast visual perception. Practise taking in a scene quickly and identifying the most relevant information. Avoid staring at one small part of the image; train yourself to take a broad initial scan.

The aim is controlled perception, not guessing. With practice, candidates often become better at noticing categories of information quickly.`,
        links: [{ label: 'Practise ATAVT-style perception', to: '/tests/atAVT' }],
      },
      {
        id: 'passing-9',
        question: 'How do I prepare for the SJE?',
        answer: `For SJE preparation, think like a safety-critical employee. Strong responses usually prioritise safety, rules, honesty, communication, responsibility, escalation when appropriate and professional behaviour.

Weak responses often involve ignoring a concern, taking shortcuts, hiding mistakes, guessing, acting outside competence, blaming others or prioritising convenience over safety.`,
        links: [{ label: 'Prepare for online assessment and SJE areas', href: TDF_ONLINE }],
      },
      {
        id: 'passing-10',
        question: 'How do I prepare for the MMI?',
        answer: `Prepare real examples from your life and work. The MMI is not just about saying you are safe, calm or responsible. You need evidence.

Use structured examples covering situations such as following rules, concentrating for long periods, dealing with pressure, communicating clearly, responding to mistakes, taking responsibility and staying calm during difficult circumstances.

Use the STAR method: Situation, Task, Action, Result. Make your own actions clear and avoid vague team-based answers.`,
        links: [{ label: 'Prepare for the MMI interview', href: TDF_INTERVIEW }],
      },
    ],
  },
  {
    id: 'rules',
    name: 'Attempts, Validity & Rules',
    icon: ClipboardCheck,
    color: 'bg-orange-500',
    questions: [
      {
        id: 'rules-1',
        question: 'How many attempts do I get at train driver psychometric tests?',
        answer: `For candidates starting the standardised psychometric assessment process from 7 December 2024, the maximum is three attempts.

If you undertook any part of the standardised psychometric assessment process before 7 December 2024, the previous two-attempt rule may still apply to you. Train companies will check your assessment history.`,
      },
      {
        id: 'rules-2',
        question: 'How long do I have to wait after failing?',
        answer: `If you fail your first attempt, your second attempt can take place a minimum of one year after the first failed attempt.

If you fail your second attempt, the third and final attempt can only take place at least five years after the first failed attempt and at least one year after the second failed attempt.`,
      },
      {
        id: 'rules-3',
        question: 'If I fail one test, do I fail the whole assessment?',
        answer: `In practical terms, failing a required part of the standardised process can stop progression. Some companies may only require you to resit failed or outstanding elements on a later attempt, while others may require more of the process to be completed again.

On a third and final attempt, the full psychometric assessment process must be covered.`,
      },
      {
        id: 'rules-4',
        question: 'How long are train driver psychometric test results valid for?',
        answer: `From RIS-3751-TOM Issue 4, train driver psychometric assessment results are valid for five years from the date of each assessment.

Validity applies until the start of driver training. After training starts, the train operator's safety management and competence management systems apply.`,
      },
      {
        id: 'rules-5',
        question: 'Can a train company use a higher pass mark?',
        answer: `Yes. RIS-3751-TOM sets the standardised process, but train companies may determine the pass marks they use. Some operators may apply an enhanced standard or use additional assessment methods outside the standardised process.`,
      },
      {
        id: 'rules-6',
        question: 'Can I appeal a failed psychometric result?',
        answer: `You should contact the train company you applied to. They will explain their appeals or review process.

Appeals usually need to focus on process concerns, mitigating circumstances, administration problems or procedural issues, rather than simply disagreeing with the result.`,
      },
      {
        id: 'rules-7',
        question: 'Can I get feedback after my assessment?',
        answer: `You can request feedback from the train company or assessment centre. The level of feedback may vary. Some companies provide limited pass/fail information, while others may give broader performance feedback.

When requesting feedback, include your full name, the date of assessment and the role or recruitment campaign you applied for.`,
      },
      {
        id: 'rules-8',
        question: 'Can I have more than one train driver application active at the same time?',
        answer: `It is usually better to progress one application and assessment process at a time. Your psychometric assessment results and attempts can affect future applications because assessment history may be checked.

Multiple overlapping applications can create confusion, especially if one operator books you into an assessment while another process is already underway.`,
      },
    ],
  },
  {
    id: 'assessment-day',
    name: 'Assessment Day',
    icon: Train,
    color: 'bg-red-500',
    questions: [
      {
        id: 'day-1',
        question: 'What happens on a train driver assessment day?',
        answer: `The exact format varies by train company and assessment provider. You may complete paper-based tests, computer-based tests, interviews or staged exercises. Some operators split the process across more than one day.

You should receive instructions before attending, including what to bring, where to go, timings, practice materials and any relevant assessment-day arrangements.`,
      },
      {
        id: 'day-2',
        question: 'What should I bring to the assessment?',
        answer: `Follow the instructions from the train company. As a minimum, candidates are commonly asked to bring photo ID and proof of National Insurance number.

You may also need glasses or contact lenses if required, medication, food and drink, and any documentation requested by the recruitment team.`,
      },
      {
        id: 'day-3',
        question: 'What should I wear?',
        answer: `Smart casual is normally suitable unless the train company gives specific instructions. Choose clothing that is professional but comfortable enough for a long assessment day.`,
      },
      {
        id: 'day-4',
        question: 'Will I get breaks during the assessment?',
        answer: `Assessment centres normally structure the day with breaks, but the exact arrangements vary. Listen carefully to instructions and use breaks properly: drink water, reset your focus and avoid over-discussing test content with other candidates.`,
      },
      {
        id: 'day-5',
        question: 'What if something goes wrong on the day?',
        answer: `Tell the assessor or assessment centre immediately. Examples could include illness, equipment failure, fire alarm disruption, unexpected personal circumstances or a problem with test materials.

Do not wait until after receiving a result to raise an issue that affected your performance. Raise concerns at the time so they can be recorded and managed properly.`,
      },
      {
        id: 'day-6',
        question: 'Can I use a calculator, notes or phone?',
        answer: `Only use materials that the assessor explicitly permits. Phones are normally switched off and put away. Test security is taken seriously, and using unauthorised materials can compromise your assessment.`,
      },
    ],
  },
  {
    id: 'adjustments',
    name: 'Neurodiversity & Adjustments',
    icon: Users,
    color: 'bg-purple-500',
    questions: [
      {
        id: 'adjustments-1',
        question: 'Can I request reasonable adjustments?',
        answer: `Yes. If you have a disability, health condition or neurodiverse condition that may affect your assessment, contact the recruitment team as early as possible. This gives the train company and assessment provider time to consider suitable adjustments.

You may be asked to provide evidence. Adjustments are considered case by case.`,
      },
      {
        id: 'adjustments-2',
        question: 'Should I disclose dyslexia, ADHD, autism, dyspraxia or dyscalculia?',
        answer: `Disclosure is your choice. However, if you may need support or reasonable adjustments, you should raise it early with the recruitment team. Employers are expected to consider reasonable adjustments when they are aware of a relevant condition.

Disclosing a condition should not be used to treat you less favourably. The purpose is to consider whether appropriate support is needed for a fair assessment process.`,
      },
      {
        id: 'adjustments-3',
        question: 'Will reasonable adjustments make the test easier?',
        answer: `No. Reasonable adjustments are intended to provide fair access to the assessment, not to lower the required standard. The assessment still needs to remain valid, reliable and fair.

The adjustment should help remove unnecessary barriers while still assessing the aptitude required for safe train driving.`,
      },
      {
        id: 'adjustments-4',
        question: 'What evidence might I need for adjustments?',
        answer: `The train company or assessment centre will tell you what evidence is required. This may include medical evidence, an educational psychologist report, occupational health information or other documentation relevant to the adjustment request.

Raise this early. If you leave it until the assessment day, support may not be possible and your assessment may need to be rescheduled.`,
      },
    ],
  },
  {
    id: 'application',
    name: 'Applications & Eligibility',
    icon: GraduationCap,
    color: 'bg-cyan-500',
    questions: [
      {
        id: 'app-1',
        question: 'How do I apply to become a train driver?',
        answer: `Apply through train company careers websites or job adverts. Most recruitment is campaign-based, meaning vacancies open for limited periods and may close quickly.

Before applying, check depot distance requirements, eligibility criteria, shift expectations and whether the role is trainee driver, apprentice driver, depot driver or qualified driver.`,
        links: [{ label: 'Visit Train Driver Foundation for career guidance', href: TDF_MAIN }],
      },
      {
        id: 'app-2',
        question: 'Do I need railway experience to pass psychometrics?',
        answer: `No. Psychometric tests assess aptitude rather than railway experience. Many successful candidates come from outside the railway.

However, railway knowledge can help at other stages, especially application forms, DMI interviews and motivation-based questions. TDF courses and guidance can help candidates understand the role properly.`,
        links: [{ label: 'Explore Train Driver Foundation', href: TDF_MAIN }],
      },
      {
        id: 'app-3',
        question: 'What qualifications do I need?',
        answer: `Train driver recruitment generally has relatively few academic prerequisites compared with many professional roles. The recruitment process is designed to identify whether you have the required aptitude, behaviour, medical fitness and suitability.

Train companies may set their own entry requirements, so always check the advert carefully.`,
      },
      {
        id: 'app-4',
        question: 'What age do I need to be?',
        answer: `The minimum age for obtaining a GB mainline train driving licence has historically been 20, although industry and government discussions have considered reducing this to 18. Always check the current advert and official licensing guidance before applying.`,
      },
      {
        id: 'app-5',
        question: 'Does depot distance matter?',
        answer: `Yes. Many train companies require candidates to live within a stated distance or travel time of the depot. This is usually linked to fatigue management, early starts, late finishes, disruption and emergency arrangements.

Do not assume relocation will be accepted. Check the advert before applying.`,
      },
      {
        id: 'app-6',
        question: 'Can English not being my first language affect my application?',
        answer: `You can apply if English is not your first language, but communication is safety-critical. You must be able to understand instructions, communicate clearly and respond appropriately in routine, degraded and emergency situations.`,
      },
    ],
  },
  {
    id: 'qualified-drivers',
    name: 'Qualified Drivers',
    icon: Route,
    color: 'bg-slate-600',
    questions: [
      {
        id: 'qd-1',
        question: 'I am already a qualified driver. Do I need to sit psychometrics again?',
        answer: `Not always. If you have previous GB mainline driving experience and a current valid licence, the recruiting operator should consider your licence status, previous assessment history, safety record and competence information.

The operator may decide no psychometric assessment is required, a bespoke assessment is required, or the full assessment is required.`,
      },
      {
        id: 'qd-2',
        question: 'What is Transfer of Safety Information?',
        answer: `Transfer of Safety Information is the process used when a candidate has previous train driving experience or has held a train driving licence. It allows recruiting operators to request relevant safety, competence, training, route, traction and driving-history information from current or former employers.`,
      },
      {
        id: 'qd-3',
        question: 'What is an Assessment Needs Analysis?',
        answer: `An Assessment Needs Analysis, or ANA, is used to decide what assessment is appropriate for a candidate with previous driving experience. It considers factors such as previous driving experience, licence status, safety record, competence history, time away from driving and differences between previous and new operations.`,
      },
    ],
  },
  {
    id: 'standards',
    name: 'Standards & Evidence',
    icon: Scale,
    color: 'bg-amber-500',
    questions: [
      {
        id: 'standards-1',
        question: 'What is RIS-3751-TOM?',
        answer: `RIS-3751-TOM is the Rail Industry Standard covering psychometric assessment in train driver selection for candidates who will drive on the GB mainline railway. Issue 4 was published in December 2024 and superseded Issue 3.

It sets out requirements and guidance for selection criteria, assessment methods, delivery, validity, reassessment, previous driving experience, audit, record keeping, data protection and security.`,
      },
      {
        id: 'standards-2',
        question: 'Is RIS-3751-TOM law?',
        answer: `A Rail Industry Standard is not legislation in the same way as an Act or Regulation, but it is an industry-agreed standard. Train operators may adopt it through internal procedures, contracts or safety management arrangements.

The standard aligns with legal and regulatory requirements, including train driving licensing and occupational psychological fitness requirements.`,
      },
      {
        id: 'standards-3',
        question: 'Who manages RIS-3751-TOM?',
        answer: `RIS-3751-TOM is published by RSSB on behalf of the rail industry and managed through the industry standards process. The Office of Rail and Road, train operators, assessment centres and other industry bodies interact with the wider regulatory and assessment framework.`,
      },
      {
        id: 'standards-4',
        question: 'What evidence supports the psychometric process?',
        answer: `The standardised process has been developed and validated through RSSB research and occupational psychology input. RIS-3751-TOM references research including T340, T628, T948 and a validation study of the train driver psychometric assessment process.

The standard focuses on reliability, validity and fairness: whether tests measure what they should measure, whether results are consistent, and whether the process avoids unjustified discrimination.`,
      },
      {
        id: 'standards-5',
        question: 'Who is allowed to administer train driver psychometric assessments?',
        answer: `Assessors must be appropriately qualified and competent. RIS-3751-TOM refers to BPS occupational ability test-user qualifications, relevant test publisher requirements, and the ORR register of recognised psychological practitioners.

This is why official train driver psychometric assessment must be carried out through authorised assessment arrangements, not by informal practice providers.`,
      },
    ],
  },
  {
    id: 'myths',
    name: 'Common Myths',
    icon: Lightbulb,
    color: 'bg-pink-500',
    questions: [
      {
        id: 'myth-1',
        question: 'Can someone give me the real answers?',
        answer: `No legitimate provider should offer real test answers. Official test materials are controlled, and test security matters because the process is safety-critical.

Good preparation means understanding the assessment areas, practising relevant skills and developing disciplined performance. It does not mean memorising leaked answers.`,
      },
      {
        id: 'myth-2',
        question: 'Do gamers automatically pass reaction and coordination tests?',
        answer: `No. Gaming experience may help some people feel comfortable with screens and hand-eye tasks, but train driver assessment looks for controlled, accurate and appropriate responses. Fast but erratic performance is not the aim.`,
      },
      {
        id: 'myth-3',
        question: 'Is the MMI just a normal interview?',
        answer: `No. The MMI is structured and evidence-based. It is designed to assess behavioural aptitude and communication. Vague answers, invented examples or generic claims usually perform badly.

You need specific examples showing what you did, why you did it, how you managed risk and what the outcome was.`,
      },
      {
        id: 'myth-4',
        question: 'Can I pass by being a train enthusiast?',
        answer: `Interest in trains is not enough. Train companies are looking for safe, reliable, disciplined candidates who can follow rules, manage concentration, communicate clearly and handle responsibility.

Railway interest can support motivation, but it does not replace aptitude, behaviour and professionalism.`,
      },
      {
        id: 'myth-5',
        question: 'Does failing mean I can never become a train driver?',
        answer: `Not necessarily. It depends on when you failed, how many attempts you have used, which rule applies to you, and whether enough time has passed before reassessment.

A failed result should be treated seriously because attempts are limited, but it does not automatically mean the career is impossible unless your attempts are exhausted.`,
      },
    ],
  },
];

const normalise = (value) => value.toLowerCase().trim();

const FAQPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  const totalQuestions = FAQ_CATEGORIES.reduce((acc, category) => acc + category.questions.length, 0);

  const filteredCategories = useMemo(() => {
    const query = normalise(searchQuery);

    return FAQ_CATEGORIES.map((category) => ({
      ...category,
      questions: category.questions.filter((item) => {
        const searchText = normalise(`${item.question} ${item.answer}`);
        const matchesSearch = !query || searchText.includes(query);
        const matchesCategory = activeCategory === 'all' || activeCategory === category.id;
        return matchesSearch && matchesCategory;
      }),
    })).filter((category) => category.questions.length > 0);
  }, [searchQuery, activeCategory]);

  const filteredCount = filteredCategories.reduce((acc, category) => acc + category.questions.length, 0);

  const popularSearches = [
    'Group Bourdon',
    'MMI',
    'SJE',
    'failed',
    'three attempts',
    'validity',
    'neurodiversity',
    'ATAVT',
    'TRP',
    'vigilance',
  ];

  return (
    <PageLayout>
      <SEO {...PageSEO.faq} />

      <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50" data-testid="faq-page">
        <section className="relative overflow-hidden bg-[#0B1D51] text-white">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,159,28,0.22),transparent_35%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.18),transparent_35%)]" />
          <div className="container relative mx-auto px-4 py-14 sm:py-20">
            <div className="mx-auto max-w-5xl text-center">
              <Badge className="mb-5 bg-[#FF9F1C] text-white hover:bg-[#FF9F1C]">
                Complete Train Driver Psychometrics FAQ
              </Badge>

              <h1 className="font-heading text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
                Train Driver Psychometric Tests Explained
              </h1>

              <p className="mx-auto mt-5 max-w-3xl text-lg leading-relaxed text-white/82 sm:text-xl">
                Clear answers on what the tests are, what they measure, how attempts work, how to prepare,
                what happens if you fail, and how the process links to RIS-3751-TOM.
              </p>

              <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
                <Link to="/tests">
                  <Button size="lg" className="w-full bg-[#FF9F1C] text-white hover:bg-[#FF9F1C]/90 sm:w-auto">
                    Start Practising Tests
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>

                <a href={TDF_MAIN} target="_blank" rel="noopener noreferrer">
                  <Button
                    size="lg"
                    variant="outline"
                    className="w-full border-white/40 bg-white/10 text-white hover:bg-white/20 sm:w-auto"
                  >
                    Visit Train Driver Foundation
                    <ExternalLink className="ml-2 h-4 w-4" />
                  </Button>
                </a>
              </div>

              <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {QUICK_STATS.map((stat) => {
                  const Icon = stat.icon;
                  return (
                    <div key={stat.label} className="rounded-2xl border border-white/15 bg-white/10 p-4 backdrop-blur">
                      <Icon className="mx-auto mb-2 h-6 w-6 text-[#FF9F1C]" />
                      <p className="text-sm text-white/65">{stat.label}</p>
                      <p className="mt-1 font-semibold text-white">{stat.value}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        <section className="border-b border-slate-200 bg-white py-8">
          <div className="container mx-auto px-4">
            <div className="mx-auto max-w-6xl">
              <div className="grid gap-4 md:grid-cols-3">
                <Card className="border-blue-100 bg-blue-50/70">
                  <CardContent className="p-5">
                    <Brain className="mb-3 h-7 w-7 text-blue-700" />
                    <h2 className="font-heading text-lg font-bold text-[#0B1D51]">What they measure</h2>
                    <p className="mt-2 text-sm leading-relaxed text-slate-700">
                      Attention, vigilance, memory, reasoning, perception, reaction time, coordination,
                      communication and behavioural aptitude.
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-green-100 bg-green-50/70">
                  <CardContent className="p-5">
                    <CheckCircle className="mb-3 h-7 w-7 text-green-700" />
                    <h2 className="font-heading text-lg font-bold text-[#0B1D51]">How to prepare</h2>
                    <p className="mt-2 text-sm leading-relaxed text-slate-700">
                      Practise accurately, build speed gradually, understand the role, prepare behavioural examples,
                      and avoid last-minute overloading.
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-amber-100 bg-amber-50/70">
                  <CardContent className="p-5">
                    <AlertCircle className="mb-3 h-7 w-7 text-amber-700" />
                    <h2 className="font-heading text-lg font-bold text-[#0B1D51]">What not to do</h2>
                    <p className="mt-2 text-sm leading-relaxed text-slate-700">
                      Do not chase leaked answers, rush for speed at the expense of accuracy, ignore instructions,
                      or treat the MMI like a casual interview.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        <section className="sticky top-16 z-40 border-b border-slate-200 bg-white/95 py-4 backdrop-blur">
          <div className="container mx-auto px-4">
            <div className="mx-auto max-w-6xl">
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Search anything: MMI, SJE, Group Bourdon, failed, attempts, neurodiversity..."
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  className="h-12 pl-10 text-sm sm:text-base"
                  data-testid="faq-search-input"
                />
              </div>

              <div className="mb-3 flex gap-2 overflow-x-auto pb-2">
                {popularSearches.map((term) => (
                  <button
                    key={term}
                    type="button"
                    onClick={() => setSearchQuery(term)}
                    className="flex-shrink-0 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:border-[#0B1D51] hover:bg-white"
                  >
                    {term}
                  </button>
                ))}
              </div>

              <div className="flex gap-2 overflow-x-auto pb-2 sm:flex-wrap sm:overflow-visible">
                <Button
                  variant={activeCategory === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setActiveCategory('all')}
                  className={`flex-shrink-0 ${activeCategory === 'all' ? 'bg-[#0B1D51]' : ''}`}
                  data-testid="faq-filter-all"
                >
                  All ({totalQuestions})
                </Button>

                {FAQ_CATEGORIES.map((category) => {
                  const Icon = category.icon;
                  return (
                    <Button
                      key={category.id}
                      variant={activeCategory === category.id ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setActiveCategory(category.id)}
                      className={`flex-shrink-0 ${activeCategory === category.id ? 'bg-[#0B1D51]' : ''}`}
                      data-testid={`faq-filter-${category.id}`}
                    >
                      <Icon className="mr-1.5 h-4 w-4" />
                      {category.name}
                      <span className="ml-1">({category.questions.length})</span>
                    </Button>
                  );
                })}
              </div>

              {searchQuery && (
                <p className="mt-2 text-sm text-slate-500">
                  Showing {filteredCount} of {totalQuestions} questions
                </p>
              )}
            </div>
          </div>
        </section>

        <section className="py-10 sm:py-14">
          <div className="container mx-auto px-4">
            <div className="mx-auto grid max-w-6xl gap-8 lg:grid-cols-[1fr_320px]">
              <div>
                {filteredCategories.length === 0 ? (
                  <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center">
                    <AlertCircle className="mx-auto mb-4 h-12 w-12 text-slate-400" />
                    <h3 className="text-lg font-semibold text-slate-800">No questions found</h3>
                    <p className="mt-2 text-slate-500">Try a different search term or clear the filters.</p>
                    <Button
                      variant="outline"
                      className="mt-5"
                      onClick={() => {
                        setSearchQuery('');
                        setActiveCategory('all');
                      }}
                    >
                      Clear filters
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-10">
                    {filteredCategories.map((category) => {
                      const Icon = category.icon;
                      return (
                        <motion.div
                          key={category.id}
                          initial={{ opacity: 0, y: 16 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.25 }}
                          data-testid={`faq-category-${category.id}`}
                        >
                          <div className="mb-4 flex items-center gap-3">
                            <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${category.color}`}>
                              <Icon className="h-5 w-5 text-white" />
                            </div>
                            <div>
                              <h2 className="font-heading text-2xl font-bold text-[#0B1D51]">
                                {category.name}
                              </h2>
                              <p className="text-sm text-slate-500">
                                {category.questions.length} question{category.questions.length === 1 ? '' : 's'}
                              </p>
                            </div>
                          </div>

                          <Accordion type="single" collapsible className="space-y-3">
  {category.questions.map((item) => (
    <AccordionItem
      key={item.id}
      value={item.id}
      className="rounded-2xl border border-slate-200 bg-white px-4 shadow-sm data-[state=open]:border-[#0B1D51]/30 data-[state=open]:bg-slate-50"
      data-testid={`faq-item-${item.id}`}
    >
      <AccordionTrigger className="py-5 text-left hover:no-underline">
        <span className="pr-4 text-lg sm:text-xl font-bold leading-snug text-[#0F172A]">
          {item.question}
        </span>
      </AccordionTrigger>

      <AccordionContent className="pb-6">
        <div className="text-base sm:text-lg leading-relaxed text-slate-700">
          {item.answer.split('\n\n').map((paragraph, index) => (
            <p key={index} className="mb-4 whitespace-pre-line last:mb-0">
              {paragraph}
            </p>
          ))}
        </div>

        {item.links?.length > 0 && (
          <div className="mt-5 flex flex-wrap gap-2">
            {item.links.map((link) => {
              const className =
                'inline-flex items-center rounded-full bg-[#0B1D51] px-4 py-2 text-sm font-medium text-white transition hover:bg-[#0B1D51]/90';

              if (link.to) {
                return (
                  <Link key={link.label} to={link.to} className={className}>
                    {link.label}
                    <ArrowRight className="ml-2 h-3.5 w-3.5" />
                  </Link>
                );
              }

              return (
                <a
                  key={link.label}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={className}
                >
                  {link.label}
                  <ExternalLink className="ml-2 h-3.5 w-3.5" />
                </a>
              );
            })}
          </div>
        )}
      </AccordionContent>
    </AccordionItem>
  ))}
</Accordion>
                        </motion.div>
                      );
                    })}
                  </div>
                )}
              </div>

              <aside className="space-y-5 lg:sticky lg:top-40 lg:self-start">
                <Card className="border-[#0B1D51]/15 bg-[#0B1D51] text-white">
                  <CardContent className="p-6">
                    <Award className="mb-3 h-8 w-8 text-[#FF9F1C]" />
                    <h3 className="font-heading text-xl font-bold">Best starting point</h3>
                    <p className="mt-2 text-sm leading-relaxed text-white/78">
                      Start with the tests most candidates worry about: Group Bourdon, TRP, ATAVT,
                      vigilance, SJE and MMI.
                    </p>
                    <Link to="/tests">
                      <Button className="mt-5 w-full bg-[#FF9F1C] text-white hover:bg-[#FF9F1C]/90">
                        View Practice Tests
                      </Button>
                    </Link>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-5">
                    <h3 className="font-heading text-lg font-bold text-[#0B1D51]">Practice areas</h3>
                    <div className="mt-4 space-y-3">
                      {TEST_LINKS.map((item) => {
                        const Icon = item.icon;
                        const content = (
                          <>
                            <Icon className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#0B1D51]" />
                            <div>
                              <p className="text-sm font-semibold text-slate-900">{item.title}</p>
                              <p className="text-xs leading-relaxed text-slate-500">{item.description}</p>
                            </div>
                          </>
                        );

                        if (item.to) {
                          return (
                            <Link
                              key={item.title}
                              to={item.to}
                              className="flex gap-3 rounded-xl border border-slate-200 p-3 transition hover:border-[#0B1D51]/40 hover:bg-slate-50"
                            >
                              {content}
                            </Link>
                          );
                        }

                        return (
                          <a
                            key={item.title}
                            href={item.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex gap-3 rounded-xl border border-slate-200 p-3 transition hover:border-[#0B1D51]/40 hover:bg-slate-50"
                          >
                            {content}
                          </a>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-amber-200 bg-amber-50">
                  <CardContent className="p-5">
                    <AlertCircle className="mb-3 h-6 w-6 text-amber-700" />
                    <h3 className="font-semibold text-amber-950">Important</h3>
                    <p className="mt-2 text-sm leading-relaxed text-amber-900">
                      TDF practice tools help candidates prepare relevant skills. They are not the official
                      assessment and cannot guarantee a pass.
                    </p>
                  </CardContent>
                </Card>
              </aside>
            </div>
          </div>
        </section>

        <section className="border-y border-slate-200 bg-white py-10 sm:py-14">
          <div className="container mx-auto px-4">
            <div className="mx-auto max-w-6xl">
              <div className="mb-7 text-center">
                <Badge className="mb-3 bg-slate-100 text-slate-700 hover:bg-slate-100">
                  Recommended Preparation Path
                </Badge>
                <h2 className="font-heading text-3xl font-bold text-[#0B1D51]">
                  A sensible way to prepare
                </h2>
                <p className="mx-auto mt-3 max-w-3xl text-slate-600">
                  Build the right skills without burning yourself out or relying on guesswork.
                </p>
              </div>

              <div className="grid gap-4 md:grid-cols-4">
                {[
                  {
                    title: '1. Understand',
                    text: 'Learn what each test measures and how it connects to the train driver role.',
                    icon: BookOpen,
                  },
                  {
                    title: '2. Practise',
                    text: 'Use focused practice to build accuracy, consistency and familiarity.',
                    icon: Activity,
                  },
                  {
                    title: '3. Review',
                    text: 'Track mistakes, slow down where needed and avoid repeating weak habits.',
                    icon: ClipboardCheck,
                  },
                  {
                    title: '4. Perform',
                    text: 'Attend rested, follow instructions exactly and prioritise controlled accuracy.',
                    icon: CheckCircle,
                  },
                ].map((step) => {
                  const Icon = step.icon;
                  return (
                    <Card key={step.title} className="border-slate-200">
                      <CardContent className="p-5">
                        <Icon className="mb-4 h-7 w-7 text-[#FF9F1C]" />
                        <h3 className="font-heading text-lg font-bold text-[#0B1D51]">{step.title}</h3>
                        <p className="mt-2 text-sm leading-relaxed text-slate-600">{step.text}</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        <section className="bg-amber-50 py-8">
          <div className="container mx-auto px-4">
            <div className="mx-auto flex max-w-5xl items-start gap-4 rounded-2xl border border-amber-200 bg-white p-5">
              <AlertCircle className="mt-0.5 h-6 w-6 flex-shrink-0 text-amber-600" />
              <div>
                <h3 className="font-semibold text-amber-950">Standards note</h3>
                <p className="mt-1 text-sm leading-relaxed text-amber-900">
                  This page explains the train driver psychometric process in candidate-friendly language.
                  Always follow the instructions from the train company or assessment centre you are applying through.
                  For official standards information, refer to RSSB and ORR.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-white py-10 sm:py-14">
          <div className="container mx-auto px-4">
            <div className="mx-auto max-w-6xl">
              <h2 className="mb-6 text-center font-heading text-2xl font-bold text-[#0B1D51]">
                Useful Resources
              </h2>

              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {[
                  {
                    title: 'TDF Psychometric Tests',
                    subtitle: 'Interactive practice tools',
                    to: '/tests',
                    icon: Brain,
                  },
                  {
                    title: 'Train Driver Foundation',
                    subtitle: 'Career guidance and courses',
                    href: TDF_MAIN,
                    icon: Train,
                  },
                  {
                    title: 'RSSB',
                    subtitle: 'Standards and research',
                    href: RSSB,
                    icon: BookOpen,
                  },
                  {
                    title: 'ORR',
                    subtitle: 'Licensing and regulation',
                    href: ORR,
                    icon: Scale,
                  },
                ].map((resource) => {
                  const Icon = resource.icon;
                  const className =
                    'flex items-center gap-3 rounded-2xl border border-slate-200 p-4 transition hover:border-[#0B1D51]/40 hover:bg-slate-50';

                  const content = (
                    <>
                      <Icon className="h-8 w-8 flex-shrink-0 text-[#0B1D51]" />
                      <div>
                        <p className="font-semibold text-[#0B1D51]">{resource.title}</p>
                        <p className="text-xs text-slate-500">{resource.subtitle}</p>
                      </div>
                      {resource.href ? (
                        <ExternalLink className="ml-auto h-4 w-4 text-slate-400" />
                      ) : (
                        <ArrowRight className="ml-auto h-4 w-4 text-slate-400" />
                      )}
                    </>
                  );

                  if (resource.to) {
                    return (
                      <Link key={resource.title} to={resource.to} className={className}>
                        {content}
                      </Link>
                    );
                  }

                  return (
                    <a
                      key={resource.title}
                      href={resource.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={className}
                    >
                      {content}
                    </a>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        <section className="bg-[#0B1D51] py-14 text-white sm:py-18">
          <div className="container mx-auto px-4 text-center">
            <div className="mx-auto max-w-3xl">
              <Train className="mx-auto mb-4 h-12 w-12 text-[#FF9F1C]" />
              <h2 className="font-heading text-3xl font-bold sm:text-4xl">
                Ready to prepare properly?
              </h2>
              <p className="mt-4 text-lg leading-relaxed text-white/80">
                Practise the core skills linked to train driver psychometric assessment and use TDF guidance
                for the wider recruitment process.
              </p>

              <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row">
                <Link to="/tests">
                  <Button size="lg" className="w-full bg-[#FF9F1C] text-white hover:bg-[#FF9F1C]/90 sm:w-auto">
                    View All Practice Tests
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>

                <a href={TDF_MAIN} target="_blank" rel="noopener noreferrer">
                  <Button
                    size="lg"
                    variant="outline"
                    className="w-full border-white/40 bg-white/10 text-white hover:bg-white/20 sm:w-auto"
                  >
                    Visit TDF
                    <ExternalLink className="ml-2 h-4 w-4" />
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
    </PageLayout>
  );
};

export default FAQPage;